import { useTranslation } from "@/hooks/useTranslation";
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import FluidImage from "../images/fluid-image";
import { FLUID_CDN_URL } from "@/lib/constants";
import { useRouter } from "next/router";
import { ChevronDown } from "lucide-react";
import { languages } from "@/lib/constants";

function LogoLoader() {
  return (
    <div className="animate-pulse">
      <div className="h-8 w-8 rounded bg-gray-200"></div>
    </div>
  );
}

function LanguageLoader() {
  return (
    <div className="animate-pulse">
      <div className="h-8 w-20 rounded bg-gray-200"></div>
    </div>
  );
}

export default function Header({
  isLoading,
  companyLogoUrl,
  handleLanguageChange,
  shopUrl,
  companyName,
  showBackButton,
  backButtonDestination,
}: {
  isLoading: boolean;
  companyLogoUrl?: string;
  handleLanguageChange?: (language: string) => void;
  shopUrl?: string;
  companyName?: string;
  showBackButton?: boolean;
  backButtonDestination?: string;
}) {
  const { t, language, changeLanguage } = useTranslation();
  const router = useRouter();

  const handleBackClick = () => {
    if (backButtonDestination) {
      router.push(backButtonDestination);
    } else {
      router.push("/manage");
    }
  };

  return (
    <div className="z-50 border-gray-200 bg-white lg:sticky lg:top-0 lg:border-b">
      <div className="mx-auto flex h-[6vh] flex-row items-center justify-between px-7 py-3 md:py-6">
        {showBackButton && (
          <button
            className="flex items-center rounded-lg px-3 py-2 text-sm hover:bg-gray-50"
            onClick={handleBackClick}
          >
            <FluidImage
              src={`${FLUID_CDN_URL}/images/checkout/icons/arrow-left.svg`}
              alt="Back"
              width={12}
              height={12}
              className="mr-1"
            />
            <span className="text-xs">{t("back")}</span>
          </button>
        )}
        <a href={shopUrl || ""} className="flex items-center">
          <span className="sr-only">{companyName ?? t("your_company")}</span>
          {isLoading ? (
            <LogoLoader />
          ) : companyLogoUrl ? (
            <img
              src={companyLogoUrl}
              alt="Company Logo"
              width={100}
              height={30}
              className="max-h-[30px] max-w-[100px] rounded object-contain"
            />
          ) : companyName ? (
            <span className="text-lg font-bold">{companyName}</span>
          ) : null}
        </a>
        {isLoading ? (
          <LanguageLoader />
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex min-w-[100px] items-center justify-between rounded bg-gray-100 px-3 py-2 text-left text-xs">
                <span>
                  {languages?.find((lang) => lang.iso === language)?.name ||
                    "English"}
                </span>
                <ChevronDown className="h-4 w-4 text-gray-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="z-[9999] w-40">
              {languages?.map((lang) => (
                <DropdownMenuItem
                  key={lang.iso}
                  className={`cursor-pointer px-3 py-2 text-xs ${
                    language === lang.iso
                      ? "bg-black font-medium text-white"
                      : "text-gray-900 hover:bg-gray-50"
                  }`}
                  onClick={() => {
                    changeLanguage(lang.iso);
                    handleLanguageChange?.(lang.iso);
                  }}
                >
                  {lang.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
